import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { GET_WORKSPACE_MEMBERS } from '../../../../../../data/queries/members'
import { GET_WORKSPACE_INVITATIONS } from '../../../../../../data/queries/workspace'
import type { IWorkspaceMember } from '../../../../../../types/user'
import { MembershipStatus } from '../../../../../../types/user'
import { filterAsChaineUser } from '../../../../../../utils/chaine-workspace-utils'

/**
 * This custom hook is used to get the workspace members and invitations.
 * It allows the user to get the workspace members and invitations.
 */
export const useWorkspaceMembersAndInvitations = (workspaceID: string, selectedWorkspaceID: string) => {
  const customHeaderContext = {
    headers: workspaceID ? { 'ws-id': workspaceID } : {}
  }

  const { data: workspaceMembersData } = useQuery(GET_WORKSPACE_MEMBERS, {
    context: customHeaderContext,
    variables: {
      input: {
        membershipStatuses: [MembershipStatus.ACTIVE, MembershipStatus.INVITED]
      }
    }
  })

  const { data: invitationsData, refetch: refetchWorkspaceInvitations } = useQuery(GET_WORKSPACE_INVITATIONS, {
    context: customHeaderContext,
    skip: !workspaceID,
    variables: { workspaceID }
  })

  const filteredWorkspaceMembers = useMemo(
    () =>
      workspaceMembersData
        ? workspaceMembersData.workspaceMembers
            ?.filter((w: { user: IWorkspaceMember['user'] }) => Boolean(w.user))
            .filter(
              (w: { user: IWorkspaceMember['user'] }) =>
                !filterAsChaineUser({ email: w.user?.email, selectedWorkspaceID })
            )
        : undefined,
    [workspaceMembersData, selectedWorkspaceID]
  )

  const filteredInvitations = useMemo(
    () =>
      invitationsData?.workspaceInvitations
        ? invitationsData.workspaceInvitations.filter(
            (i: { user: { email: string } }) => !filterAsChaineUser({ email: i.user.email, selectedWorkspaceID })
          )
        : undefined,
    [invitationsData, selectedWorkspaceID]
  )

  return {
    refetchWorkspaceInvitations,
    workspaceInvitations: filteredInvitations || [],
    workspaceMembers: filteredWorkspaceMembers || []
  }
}
